import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';

const SESSION_TIMEOUT_MINUTES = 30; // 30 minutes for admin users
const WARNING_TIME_MINUTES = 5; // Show warning 5 minutes before timeout

export const useSessionTimeout = () => {
  const { user, isAdmin } = useAuth();
  const { toast } = useToast();
  const [timeLeft, setTimeLeft] = useState<number>(SESSION_TIMEOUT_MINUTES * 60);
  const [showWarning, setShowWarning] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const resetTimer = useCallback(() => {
    setTimeLeft(SESSION_TIMEOUT_MINUTES * 60);
    setShowWarning(false);
  }, []);

  const handleActivity = useCallback(() => {
    if (isAdmin && isActive) {
      resetTimer();
    }
  }, [isAdmin, isActive, resetTimer]);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    toast({
      title: "Sesión expirada",
      description: "Tu sesión ha expirado por inactividad. Por favor, inicia sesión nuevamente.",
      variant: "destructive",
    });
  }, [toast]);

  const extendSession = useCallback(() => {
    resetTimer();
    toast({
      title: "Sesión extendida",
      description: "Tu sesión ha sido extendida por 30 minutos más.",
    });
  }, [resetTimer, toast]);

  useEffect(() => {
    // Only activate session timeout for admin users
    if (user && isAdmin) {
      setIsActive(true);
      resetTimer();
    } else {
      setIsActive(false);
    }
  }, [user, isAdmin, resetTimer]);

  useEffect(() => {
    if (!isActive) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        const newTime = prev - 1;
        
        // Show warning 5 minutes before timeout
        if (newTime === WARNING_TIME_MINUTES * 60 && !showWarning) {
          setShowWarning(true);
          toast({
            title: "Advertencia de sesión",
            description: `Tu sesión expirará en ${WARNING_TIME_MINUTES} minutos por inactividad.`,
            variant: "destructive",
          });
        }
        
        // Logout when time reaches 0
        if (newTime <= 0) {
          logout();
          return 0;
        }
        
        return newTime;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isActive, showWarning, toast, logout]);

  useEffect(() => {
    if (!isActive) return;

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [handleActivity, isActive]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return {
    timeLeft: formatTime(timeLeft),
    showWarning,
    extendSession,
    isActive
  };
};